import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Phone } from "lucide-react";
import api from "@/lib/api";
import { CONTACT } from "@/data/site";

const QUICK = ["Book a consultation", "Get a quote", "How does the AI studio work?"];

export const RossiAssistant = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Ciao! I'm the Rossi Assistant. Ask me about our tiling, bathrooms, wet rooms, the AI Design Studio, quotes or bookings." },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: content, history: next.slice(-8) });
      setMessages([...next, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: `Sorry, I had trouble. Please call Vincent on ${CONTACT.phone}.` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="assistant-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed z-40 right-4 bottom-20 lg:bottom-6 h-14 w-14 rounded-full bg-rossi-gold text-rossi-bg flex items-center justify-center shadow-xl hover:bg-rossi-goldhover transition-colors"
        aria-label="Open Rossi Assistant"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div data-testid="assistant-panel" className="fixed z-40 right-4 bottom-36 lg:bottom-24 w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] card-lux flex flex-col overflow-hidden shadow-2xl">
          <div className="p-4 border-b border-rossi-gold/15 bg-rossi-surface flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-rossi-gold/20 flex items-center justify-center"><MessageCircle size={18} className="text-rossi-gold" /></div>
            <div>
              <div className="font-serif text-lg text-rossi-cream leading-none">Rossi Assistant</div>
              <div className="text-[11px] text-rossi-stone mt-1">Typically replies instantly</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-rossi-gold text-rossi-bg" : "bg-rossi-surface text-rossi-cream/90 border border-white/5"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && <div className="text-xs text-rossi-stone font-mono">Rossi Assistant is typing…</div>}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => send(q)} className="text-xs border border-rossi-gold/30 text-rossi-cream/80 rounded-full px-3 py-1.5 hover:bg-rossi-gold/10 transition-colors">{q}</button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-rossi-gold/15 bg-rossi-surface">
            <div className="flex items-center gap-2">
              <input
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about your project…"
                className="flex-1 bg-rossi-bg border border-white/10 rounded-full px-4 py-2.5 text-sm text-rossi-cream placeholder:text-rossi-stone focus:outline-none focus:border-rossi-gold/50"
              />
              <button data-testid="assistant-send" onClick={() => send()} disabled={loading} className="h-10 w-10 rounded-full bg-rossi-gold text-rossi-bg flex items-center justify-center disabled:opacity-50"><Send size={16} /></button>
            </div>
            <a href={CONTACT.phoneHref} className="mt-2 flex items-center justify-center gap-1.5 text-xs text-rossi-stone hover:text-rossi-gold transition-colors"><Phone size={12} /> Prefer to talk? Call {CONTACT.phone}</a>
          </div>
        </div>
      )}
    </>
  );
};

export default RossiAssistant;
